import React, { useCallback, FC, useEffect } from 'react'
import { getParentDepartment } from '@moonpig/web-core-utils'
import { Box, Container, Flex } from '@moonpig/launchpad-components'
import { styled, breakpointUp, breakpointDown } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { Breadcrumbs } from '@moonpig/web-core-nav'
import {
  useHandleAddToBasket,
  AddedToBasketEvent,
  DispatchBanner,
  useBasket,
  DispatchCutOffQuery,
  PageTypeEnum,
  ProductTileProduct,
} from '@moonpig/web-shared-products'
import { useCustomerId } from '@moonpig/web-shared-utils'
import { trackGAEvent } from '@moonpig/web-core-analytics'
import { DepartmentsEnum } from '@moonpig/web-explore-types-graphql'
import { logger } from '@moonpig/web-core-monitoring'
import { CmsContent, SearchFiltersFacet } from '../../pages/GalleryPage/types'
import { generateBreadcrumbsClickGAEvent } from '../../analytics/GAEvents'
import { FixedBottomBar } from '../FixedBottomBar'
import { SoldOutGalleryMessage } from './SoldOutGalleryMessage'
import { Header } from './Header'
import { ProductGridWithTracking } from '../ProductGridWithTracking'
import { OfferBanner } from '../OfferBanner'
import { getSortByOptions, useResetOffset, useSetUIHCookie } from '../../utils'
import { useFindLocaleText } from '../../text-localisation'
import { usePromoTile } from './usePromoTile'
import { FiltersMenu } from '../FiltersMenu'
import { useSearchStore } from '../../store/SearchStore'
import { InternalUserGalleryInfo } from '../InternalUserGalleryInfo'
import { FiltersBar } from '../FiltersBar/FiltersBar'

const StyledBox = styled(Box)`
  ${s({
    bgcolor: 'colorBackground01',
  })}
`

const StyledHeader = styled(Container)`
  ${s({
    paddingTop: 4,
    paddingLeft: { xs: 6, xl: 8 },
    marginX: 'auto',
  })}
`

const StyledFlex = styled(Flex)`
  width: 100%;
  flex-direction: row;

  ${breakpointDown('md')} {
    flex-direction: column;
  }
`

const StyledGridContainer = styled(Container)`
  position: relative;
  width: 100%;
`

const TrackableDiv = styled.div`
  flex: 0 0 auto;
  ${s({
    mb: 4,
  })}
`

const StyledTextContainer = styled(Box)`
  ${breakpointUp('md')} {
    ${s({
      p: 4,
      m: 6,
      minHeight: '66px',
    })}
  }

  a {
    ${s({
      typography: 'typeBodyLabel',
    })}
  }
`

type GalleryProps = {
  content: CmsContent
  metaTitle: string
  products: ProductTileProduct[]
  presetFacets: SearchFiltersFacet[]
  experiments?: { [key: string]: string } | undefined
  dispatchCutOffData?: DispatchCutOffQuery
  shouldDisplaySoldOutMessage?: boolean
  groupCardProject?: string
  totalClickRankDocumentCount: number
  isInternalUser: boolean
  totalCount: number
}

export const Gallery: FC<GalleryProps> = ({
  content,
  products,
  presetFacets,
  experiments,
  dispatchCutOffData,
  shouldDisplaySoldOutMessage,
  metaTitle,
  groupCardProject,
  totalClickRankDocumentCount,
  isInternalUser,
  totalCount,
}) => {
  useResetOffset(0)
  const [_, { setLoading, setSuccess, setError }] = useBasket()
  const customerId = useCustomerId()
  const localiseText = useFindLocaleText()
  const { setUIHCookie } = useSetUIHCookie()
  const selectedFilters = useSearchStore(store => store.selectedFilters)
  const preAppliedFilters = useSearchStore(store => store.preAppliedFilters)
  const searchContext = useSearchStore(store => store.searchContext)
  const loadingProducts = useSearchStore(store => store.loading)

  const { title, description, breadcrumbsItems, offer } = content
  const { promotionId, searchTerm, departments } = searchContext
  const isCards =
    getParentDepartment(departments[0]) === DepartmentsEnum.ALL_CARDS

  const offerBanner = OfferBanner({ offerText: offer })

  const soldOutMessage = localiseText('find.sold_out')
  const itsNotYouMessage = localiseText('find.its_not_you')

  const { handleAddToBasket, addingToBasket } = useHandleAddToBasket({
    pageLocation: 'gallery',
    soldOutMessage,
    itsNotYouMessage,
    groupCardProject,
  })

  useEffect(() => {
    setLoading(addingToBasket)
  }, [addingToBasket, setLoading])

  const onAddedToBasket = useCallback(
    async (e: AddedToBasketEvent) => {
      const {
        product,
        variant,
        quantity,
        selectedAddon,
        buttonActionName,
        requiresEditingValue,
        componentName,
        redirectUser,
        giftXSellTrackingData,
      } = e
      const allAppliedFilters = [
        ...selectedFilters.map(f => ({ facetKey: f.id, group: f.parent })),
        ...preAppliedFilters.map(f => ({
          facetKey: f.key,
          group: f.group,
        })),
      ]
      setUIHCookie({
        product,
        facets: allAppliedFilters,
      })
      try {
        await handleAddToBasket({
          quantity,
          product,
          variant,
          filters: allAppliedFilters,
          experiments: { ...experiments },
          searchTerm,
          selectedAddon,
          itemIndex: product?.productIndex || 0,
          productsLength: totalCount,
          buttonActionName,
          requiresEditingValue,
          componentName,
          redirectUser,
          giftXSellTrackingData,
        })
        setSuccess(true)
      } catch (error) {
        logger.fixToday('Failure adding product to basket', error)
        setError(true)
      }
    },
    [
      setUIHCookie,
      handleAddToBasket,
      selectedFilters,
      preAppliedFilters,
      experiments,
      searchTerm,
      totalCount,
      setSuccess,
      setError,
    ],
  )

  const promoTile = usePromoTile(content.promoTile)

  const dispatchCutOff =
    dispatchCutOffData?.contentDepartments[0]?.dispatchCutOff

  const sortByOptions = getSortByOptions({
    isCards,
    localiseText,
  })

  return (
    <StyledBox>
      {((products && products.length > 0) || loadingProducts) && (
        <>
          {offerBanner ||
            (dispatchCutOff && (
              <DispatchBanner dispatchCutOff={dispatchCutOff} />
            ))}
          <StyledHeader limitWidth>
            <Box>
              <TrackableDiv
                onClick={() => {
                  trackGAEvent(
                    generateBreadcrumbsClickGAEvent(breadcrumbsItems),
                  )
                }}
                data-testid="breadcrumb-header"
              >
                <Breadcrumbs items={breadcrumbsItems} />
              </TrackableDiv>
              <Header title={title} />
              {!loadingProducts &&
                description &&
                departments[0] === DepartmentsEnum.DIGITAL_GIFTS && (
                  <Box
                    mb={6}
                    /* eslint-disable-next-line react/no-danger */
                    dangerouslySetInnerHTML={{ __html: description }}
                    data-testid="gallery-description"
                  />
                )}
            </Box>
          </StyledHeader>
          {isInternalUser && (
            <InternalUserGalleryInfo
              productCount={totalCount}
              searchTerm={searchTerm}
              filters={presetFacets}
              promotionId={promotionId}
              totalClickRankDocumentCount={totalClickRankDocumentCount}
              departments={departments}
            />
          )}
          <FiltersMenu sortOptions={sortByOptions} />
          <FiltersBar departments={departments} />
          <StyledFlex>
            <StyledGridContainer limitWidth padding={0}>
              <ProductGridWithTracking
                totalCount={totalCount}
                searchContext={searchContext}
                pageTitle={title}
                metaTitle={metaTitle}
                products={products}
                onAddedToBasket={onAddedToBasket}
                isInternalUser={isInternalUser}
                presetFacets={presetFacets}
                experiments={experiments}
                pageType={PageTypeEnum.GALLERY}
                customerId={customerId}
                groupCardProject={groupCardProject}
                promoTile={promoTile}
              />
            </StyledGridContainer>
          </StyledFlex>
          {!loadingProducts && description && (
            <StyledTextContainer>
              <div
                /* eslint-disable-next-line react/no-danger */
                dangerouslySetInnerHTML={{ __html: description }}
                data-testid="gallery-description"
              />
            </StyledTextContainer>
          )}
        </>
      )}
      {shouldDisplaySoldOutMessage && (
        <SoldOutGalleryMessage department={departments} />
      )}
      <FixedBottomBar pageTitle={title} />
    </StyledBox>
  )
}
